import React, { useContext, useEffect, useState } from 'react';
import { Box, ButtonGroup, Flex, Text, Button, useColorModeValue, Checkbox } from '@chakra-ui/react';
import LinkButton from '../../../components/custom/LinkButton';
import AddButton from '../../../components/custom/AddButton';
import { UseConstant } from '../../../libraries/UseConstant';
import DeleteButton from '../../../components/custom/DeleteButton';
import Loading from '../../../components/common/Loading';
import NoData from '../../../components/common/NoData';
import { GetContractTypeOptionByValue, GetUserTypeOptionByValue } from '../../../components/options/GetOptionByValue';
import PageHeader from '../../../components/common/PageHeader';
import SearchBar from '../../../components/search/SearchBar';
import CustomTable from '../../../components/custom/CustomTable';
import { GetBranchOptions, GetCountryOptions, GetDepartmentOptions, getTechnologyOptions, GetUserTypeOptions } from '../../../components/options/SelectOptions';
import UserCardView from './UserCardView';
import { useNavigate } from 'react-router-dom';
import { FiGrid, FiTable } from 'react-icons/fi';
import CustomButton from '../../../components/custom/CustomButton';
import { useApi } from '../../../hooks/useApi';
import { useToast } from '../../../hooks/useToast';
import { UserInfoContext } from '../../../provider/UserInfoProvider';
import { UseApi } from '../../../libraries/UseApi';
import { UseMessage } from '../../../libraries/UseMessage';

export default function Users() {
    const { constant } = UseConstant();
    const { USER_LIST } = constant;
    const navigate = useNavigate();
    const [isProcessing, setIsProcessing] = useState(false);
    const { token } = useContext(UserInfoContext);
    const { Message } = UseMessage();

    const { get, delete: deleteRequest, patch, isLoading } = useApi();
    const { showError, showSuccess } = useToast();

    const cardBg = useColorModeValue('whiteAlpha.800', 'whiteAlpha.100');

    // 取得データ
    const [users, setUsers] = useState([]);
    const [branches, setBranches] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [countries, setCountries] = useState([]);
    const [technologies, setTechnologies] = useState([]);
    const [showSearch, setShowSearch] = useState(false);
    const [viewMode, setViewMode] = useState('card'); // 'table' or 'card'
    const [selectedTemplate, setSelectedTemplate] = useState('basic');
    const [isDownloading, setIsDownloading] = useState(false);

    // ページ番号
    const [currentPageNo, setCurrentPageNo] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const { getApi } = UseApi();

    // 検索パラメータ
    const [searchParams, setSearchParams] = useState({});

    const searchFields = [
        {
            name: 'search_name',
            label: '姓名',
            type: 'text',
        },
        {
            name: 'search_name_kana',
            label: '姓名カナ',
            type: 'text',
        },
        {
            name: 'search_user_type',
            label: 'ユーザタイプ',
            type: 'dropdown',
            options: GetUserTypeOptions(),
        },
        {
            name: 'search_branch_id',
            label: 'ユーザタイプ',
            type: 'dropdown',
            options: GetBranchOptions(branches),
        },
        {
            name: 'search_department_id',
            label: '部署',
            type: 'dropdown',
            options: GetDepartmentOptions(departments),
        },
        {
            name: 'search_country_id',
            label: '国',
            type: 'dropdown',
            options: GetCountryOptions(countries),
        },
        {
            name: 'search_available_from_start',
            label: '参加可能日～',
            type: 'date',
        },
        {
            name: 'search_available_from_end',
            label: '～参加可能日',
            type: 'date',
        },
        {
            name: 'search_technology_ids',
            label: '技術スキル',
            type: 'checkboxes',
            options: getTechnologyOptions(technologies),
        }
    ];

    const handleSearch = () => {
        setCurrentPageNo(1);
        getUsers();
    };

    // 検索処理
    const getUsers = () => {
        // 処置中チェック
        if (isProcessing) {
            // 処理中の場合、以降の処理を実施しない
            return;
        }
        // 処理中フラグをONにする
        setIsProcessing(true);

        // 検索パラメータ
        let paging = {
            limit: USER_LIST,
            offset: currentPageNo - 1
        };
        let params = { ...paging, ...searchParams };

        // 未受検者検索API
        getApi(
            `${process.env.REACT_APP_API_URL}/user`, params, { token: token },
            (response) => {
                // 成功時
                if (response.isSuccess === false) {
                    // error
                    alert(Message.Common.ERROR);
                    // 処理中フラグを元に戻す
                    setIsProcessing(false);
                    return;
                }

                setUsers(response?.data?.users || []);
                setTotalPages(Math.ceil(response?.data?.total / paging?.limit));
                // 処理中フラグを元に戻す
                setIsProcessing(false);
            },
            () => {
                // 異常時
                // 処理中フラグをOFFにする
                setIsProcessing(false);
            }
        );
    };

    const getBranchDepartmentCountryData = async () => {
        try {
            const response = await get('/common/branch-department-country-data');

            if (response.status !== "success") {
                showError('データの取得に失敗しました');
                return;
            }

            setBranches(response?.data?.branches || []);
            setDepartments(response?.data?.departments || []);
            setCountries(response?.data?.countries || []);
            setTechnologies(response?.data?.technologies || []);
        } catch (error) {
            // Silently fail for optional data
        }
    };

    useEffect(() => {
        getBranchDepartmentCountryData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDelete = async (id) => {
        try {
            const response = await deleteRequest(`/user/${id}`);

            if (response.isSuccess === false) {
                showError('削除に失敗しました');
                return;
            }

            showSuccess('削除しました');
            getUsers();
        } catch (error) {
            showError('削除に失敗しました');
        }
    };

    const handleSendInvite = async (id) => {
        try {
            const response = await patch(`/user/invite/${id}`, {});
            
            if (response.isSuccess === false) {
                showError('招待の送信に失敗しました');
                return;
            }
            
            showSuccess('招待を送信しました');
            getUsers();
        } catch (error) {
            showError('招待の送信に失敗しました');
        }
    };

    const exportUserToPdf = async (userId) => {
        setIsDownloading(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/user/export-pdf`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${sessionStorage.getItem('token')}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_id: userId,
                    template: selectedTemplate,
                }),
            });

            const blob = await res.blob();
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `user_${userId}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            showError('PDFのエクスポートに失敗しました');
        } finally {
            setIsDownloading(false);
        }
    };

    useEffect(() => {
        getUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPageNo]);

    const decideAvailable = (param_date) => {
        const cur_date = new Date();
        const input_date = new Date(param_date);
        if (cur_date >= input_date) {
            return 'green';
        } else {
            return 'red';
        }
    }

    // const handleCheck = (e, id) => {
    //     if (e.target.checked) {
    //         setChecked([...checked, id]);
    //     } else {
    //         setChecked([...checked].filter(item => item !== id));
    //     }
    //     return e;
    // }

    const handlePageChange = (page) => {
        setCurrentPageNo(page);
    }

    const columns = [
        { header: '姓名', accessor: 'name' },
        { header: '姓名カナ', accessor: 'name_kana' },
        {
            header: 'ユーザタイプ',
            Cell: (row) => GetUserTypeOptionByValue(row?.user_type),
        },
        { header: '支社', accessor: 'branch' },
        { header: '部署', accessor: 'department' },
        { header: '国', accessor: 'country' },
        {
            header: '契約タイプ',
            Cell: (user) => GetContractTypeOptionByValue(user?.contract_type),
        },
        {
            header: '参画可能日',
            Cell: (user) => (
                <Text color={decideAvailable(user?.available_from)}>
                    {user.available_from}
                </Text>
            ),
        },
        {
            header: '招待',
            Cell: (user) =>
                user.invite_count === 0 ? (
                    <Button colorScheme="yellow" onClick={() => handleSendInvite(user.id)}>
                        招待
                    </Button>
                ) : (
                    <Text color="gray">招待済み</Text>
                ),
        },
    ];

    const renderActions = (user) => (
        <>
            <CustomButton buttonText="出力" action={() => exportUserToPdf(user.id)} />
            <LinkButton colorScheme="blue" buttonText="詳細" buttonUrl={`/user/${user.id}/show`} />
            <LinkButton buttonText="編集" buttonUrl={`/user/edit/${user.id}`} />
            <DeleteButton colorScheme="red" handleDelete={handleDelete} id={user.id} />
        </>
    );

    return (
        <Box
            bgGradient="linear(to-br, orange.50, white)"
            minH="100vh"
            w="full"
        >
            {/* Page Header */}
            <Flex
                justify="space-between"
                align="center"
                mb={5}
                flexWrap="wrap"
                gap={4}
            >
                <PageHeader headerTitle="ユーザ一覧" />
                <ButtonGroup>
                    <AddButton buttonText="新規登録" buttonUrl="/add-user" />
                    <LinkButton buttonText="戻る" buttonUrl="/admin-dashboard" />
                </ButtonGroup>
            </Flex>

            {/* ✅ Search Toggle */}
            <Checkbox
                isChecked={showSearch}
                onChange={(e) => setShowSearch(e.target.checked)}
                colorScheme="orange"
                mb={4}
            >
                <Text fontSize="md" color={useColorModeValue('gray.800', 'white')}>
                    フィルターを表示
                </Text>
            </Checkbox>

            {/* Gorgeous Container */}
            <Box
                bg={cardBg}
                boxShadow="xl"
                backdropFilter="blur(10px)"
                p={{ base: 4, md: 8 }}
            >
                {/* Search */}
                {showSearch && (
                    <Box mb={6}>
                        <SearchBar
                            searchFields={searchFields}
                            searchParams={searchParams}
                            setSearchParams={setSearchParams}
                            onSearch={handleSearch}
                        />
                    </Box>
                )}

                {/* Content */}
                {isLoading ? (
                    <Loading />
                ) : users.length === 0 ? (
                    <NoData />
                ) : (
                    <>
                        <Flex justify="flex-end" mb={4}>
                            <ButtonGroup isAttached variant="outline" colorScheme="orange">
                                <Button
                                    leftIcon={<FiTable />}
                                    variant={viewMode === 'table' ? 'solid' : 'outline'}
                                    onClick={() => setViewMode('table')}
                                >
                                    表示
                                </Button>
                                <Button
                                    leftIcon={<FiGrid />}
                                    variant={viewMode === 'card' ? 'solid' : 'outline'}
                                    onClick={() => setViewMode('card')}
                                >
                                    カード
                                </Button>
                            </ButtonGroup>
                        </Flex>
                        {viewMode === 'table' ? (
                            <CustomTable
                                columns={columns}
                                data={users}
                                renderActions={renderActions}
                                paginationProps={{
                                    currentPage: currentPageNo,
                                    totalPages: totalPages,
                                    onPageChange: handlePageChange
                                }}
                            />
                        ) : (
                            <UserCardView
                                data={users}
                                handleEdit={(id) => navigate(`/user/edit/${id}`)}
                                handleDelete={(id) => handleDelete(id)}
                                handleExport={(id) => exportUserToPdf(id)}
                                selectedTemplate={selectedTemplate}
                                setSelectedTemplate={setSelectedTemplate}
                                isDownloading={isDownloading}
                                paginationProps={{
                                    currentPage: currentPageNo,
                                    totalPages: totalPages,
                                    onPageChange: handlePageChange
                                }}
                            />
                        )}
                    </>
                )}
            </Box>
        </Box>
    )
}